import React, { useEffect, useState, useMemo } from 'react';
import { Sparkles } from 'lucide-react';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useAiAction } from '@/hooks/useAiAction';
import { AppState } from '@/types';
import { addDays, parseISO, format, startOfMonth } from 'date-fns';
import { doc, getDoc, collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/firebase';
import type { MonthlyBudget, BudgetCategory } from '@/types/finance';

interface HealthScoreWidgetProps {
  state: AppState;
}

interface HealthScore {
  score: number;
  breakdown: { tasks: number; grocery: number; maintenance: number; bills: number };
  insights: string[];
}

export const HealthScoreWidget: React.FC<HealthScoreWidgetProps> = ({ state }) => {
  const householdId = state.currentHouseholdId;
  const { callAction, isLoading } = useAiAction(householdId);
  const [healthScore, setHealthScore] = useState<HealthScore | null>(null);

  const billsDueThisWeek = useMemo(() => {
    const now = new Date();
    const weekEnd = addDays(now, 7);
    return ((state as any).bills ?? []).filter((b: any) => {
      if (!b.nextDueDate || b.isPaid) return false;
      try {
        const due = parseISO(b.nextDueDate);
        return due >= now && due <= weekEnd;
      } catch { return false; }
    }).length;
  }, [(state as any).bills]);

  useEffect(() => {
    if (!householdId) return;
    const today = new Date().toISOString().slice(0, 10);
    const cacheKey = `health_score_${householdId}_${today}`;
    const cached = sessionStorage.getItem(cacheKey);
    if (cached) {
      try { setHealthScore(JSON.parse(cached)); return; } catch { /* ignore */ }
    }

    const overdueTasks = state.tasks.filter(t => t.dueDate && t.dueDate < today && t.status !== 'done' && t.status !== 'finalized').length;
    const totalTasks = state.tasks.length;
    const lowStockItems = state.inventory.filter(t => (t.consumption?.remainingQuantity ?? 1) === 0).length;
    const overdueMaintenanceCount = state.maintenanceTasks.filter(t => t.dueDate && t.dueDate < today && t.status !== 'done').length;

    // Fetch budget adherence
    const yearMonth = format(startOfMonth(new Date()), 'yyyy-MM');
    const firstDay = format(startOfMonth(new Date()), 'yyyy-MM-dd');

    const getBudgetAdherence = async (): Promise<number | null> => {
      try {
        const budgetSnap = await getDoc(doc(db, 'households', householdId, 'budgets', yearMonth));
        if (!budgetSnap.exists()) return null;
        const budget = budgetSnap.data() as MonthlyBudget;
        const txSnap = await getDocs(
          query(collection(db, 'households', householdId, 'transactions'),
            where('date', '>=', firstDay), where('type', '==', 'expense'))
        );
        const spentByCategory: Record<string, number> = {};
        txSnap.forEach(d => {
          const cat = d.data().category || 'other';
          spentByCategory[cat] = (spentByCategory[cat] || 0) + (d.data().amount || 0);
        });
        const cats = Object.entries(budget.categories).filter(([, c]: [string, BudgetCategory]) => c.ceiling > 0);
        if (cats.length === 0) return null;
        const under = cats.filter(([id, c]: [string, BudgetCategory]) => (spentByCategory[id] || 0) <= c.ceiling).length;
        return Math.round((under / cats.length) * 100);
      } catch { return null; }
    };

    getBudgetAdherence().then(budgetAdherence => {
      const payload: Record<string, unknown> = {
        overdueTasks,
        totalTasks: Math.max(totalTasks, 1),
        lowStockItems,
        overdueMaintenanceCount,
        billsDueThisWeek,
      };
      if (budgetAdherence !== null) payload.budgetAdherence = budgetAdherence;

      callAction<HealthScore>('computeHealthScore', payload).then(result => {
        if (result) {
          setHealthScore(result);
          sessionStorage.setItem(cacheKey, JSON.stringify(result));
        }
      });
    });
  }, [householdId]);

  const score = healthScore?.score ?? 0;
  const circumference = 2 * Math.PI * 40;
  const dashOffset = circumference - (score / 100) * circumference;
  const color = score >= 75 ? '#22c55e' : score >= 50 ? '#f59e0b' : '#ef4444';

  return (
    <div className="flex flex-col h-full p-4 gap-3">
      <div className="flex items-center gap-2">
        <Sparkles className="w-4 h-4 text-amber-500" />
        <h3 className="text-2xs font-black uppercase tracking-widest text-slate-500 dark:text-slate-400">Household Health</h3>
      </div>

      {isLoading && !healthScore ? (
        <div className="flex-1 flex items-center justify-center">
          <LoadingSpinner />
        </div>
      ) : (
        <div className="flex-1 flex flex-col items-center justify-center gap-3">
          <svg viewBox="0 0 100 100" className="w-28 h-28">
            <circle cx="50" cy="50" r="40" fill="none" stroke="#e2e8f0" strokeWidth="8" />
            <circle
              cx="50" cy="50" r="40" fill="none"
              stroke={color} strokeWidth="8"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={dashOffset}
              transform="rotate(-90 50 50)"
              style={{ transition: 'stroke-dashoffset 0.8s ease' }}
            />
            <text x="50" y="54" textAnchor="middle" fontSize="20" fontWeight="900" fill="currentColor" className="text-slate-900 dark:text-white">
              {score}
            </text>
          </svg>

          {healthScore?.insights && healthScore.insights.length > 0 && (
            <div className="space-y-1 w-full">
              {healthScore.insights.slice(0, 2).map((insight, i) => (
                <p key={i} className="text-2xs text-slate-500 dark:text-slate-400 text-center">{insight}</p>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
